import React from "react";
import { FormGroup, Label, Input, Col } from "reactstrap";
import { useField, ErrorMessage } from "formik";
import { FormCheckboxProps } from "./formCheckbox";
import { OptionsDto } from "../../../../utils/lib/optionsDto";

interface FormCheckboxGroupProps extends FormCheckboxProps {
  columns?: number;
  options?: Array<OptionsDto>;
  onChange?: (values: Array<string>) => void;
}

export const CheckboxGroup: React.FC<FormCheckboxGroupProps> = ({
  columns: col = 1,
  name,
  options,
  label,
  isRequired,
  onChange,
  labelStyle,
}) => {
  const [field, meta] = useField(name);
  const { onChange: fieldOnChange, ...fieldProps } = field;

  const handleCheckboxChange = (optionId: string) => {
    const currentValue: string[] = field.value ?? [];
    const updatedOptions = currentValue.includes(optionId)
      ? currentValue.filter((id: string) => id !== optionId)
      : [...currentValue, optionId];

    // Formik default handleChange
    fieldOnChange({ target: { name: field.name, value: updatedOptions } });
    // Custom onChange provided by the parent component
    if (onChange) {
      onChange(updatedOptions);
    }
  };

  const checkboxesInColumns = options?.reduce(
    (columns: OptionsDto[][], checkbox, index) => {
      const columnIndex = index % col; // Adjust the number of columns as needed
      columns[columnIndex] = columns[columnIndex] || [];
      columns[columnIndex].push(checkbox);
      return columns;
    },
    []
  );

  return (
    <FormGroup row tag="fieldset" className="mb-3">
      <Label style={{ ...labelStyle }}>
        {label}
        {isRequired && " *"}
      </Label>
      {checkboxesInColumns?.map((column, columnIndex) => (
        <Col key={columnIndex}>
          {column.map((option) => (
            <FormGroup check key={option.value}>
              <Label check>
                <Input
                  type="checkbox"
                  {...fieldProps}
                  value={option.value}
                  id={`${name}-${option.value}`}
                  name={name}
                  checked={field.value?.includes(option.value)}
                  onChange={() => handleCheckboxChange(option.value)}
                />
                {option.label}
              </Label>
            </FormGroup>
          ))}
        </Col>
      ))}

      {meta?.error && (
        <ErrorMessage
          render={(msg) => (
            <div className="invalid-feedback" style={{ display: "block" }}>
              {msg}
            </div>
          )}
          name={name}
        />
      )}
    </FormGroup>
  );
};
